import { Button, Box, IconButton, Typography } from '@mui/material';
import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import uploadCloud from './../../assets/svg/uploadCloud.svg';
import xDeleteIcon from './../../assets/svg/xDeleteIcon.svg';

import { getFileTypeIcon } from '../../assets/utils/getFileTypeIcon';

interface AddFileButtonProps {
  selectedFiles: File[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  disabled: boolean;
}

const AddFileButton = ({
  selectedFiles,
  setSelectedFiles,
  disabled,
}: AddFileButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileDelete = (fileToDelete: File) => {
    setSelectedFiles((prevSelectedFiles) =>
      prevSelectedFiles.filter((file) => file !== fileToDelete),
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = [...files];
      setSelectedFiles((prevSelectedFiles) => [
        ...prevSelectedFiles,
        ...newFiles,
      ]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    const newFiles = Array.from(files);
    setSelectedFiles((prevSelectedFiles) => [
      ...prevSelectedFiles,
      ...newFiles,
    ]);
  };

  const handleChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Trigger click event on the input
    }
  };

  return (
    <Box
      sx={{
        padding: '0rem',
        opacity: disabled ? '0.7' : '1',
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <Typography
        variant='h4'
        sx={{
          fontFamily: 'Schibsted Grotesk',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '17px',
          letterSpacing: '0em',
          textAlign: 'left',
          mt: '1rem',
          mb: '0.5rem',
        }}
      >
        <FormattedMessage defaultMessage='Upload File' />
      </Typography>
      <Box
        sx={{
          padding: '0rem',
          opacity: disabled ? '0.7' : '1',
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <Box
          sx={{
            padding: '0rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleChooseFileClick}
          style={{
            border: '2px dashed rgba(0, 206, 207, 1)',
            padding: '20px',
            textAlign: 'center',
            cursor: 'default', // Disable pointer events for the entire box
            backgroundColor: 'rgba(0, 206, 207, 0.04)',
            borderRadius: '6px',
          }}
        >
          <input
            id='file-upload'
            type='file'
            style={{ display: 'none' }}
            multiple
            onChange={handleFileChange}
            ref={fileInputRef}
          />
          <img src={uploadCloud} alt='Logo' style={{ height: 30 }} />
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0rem',
              color: '#00000080',
            }}
          >
            <FormattedMessage defaultMessage='Drag and drop files here or' />
            &nbsp;
            <Button
              variant='text'
              style={{
                textDecoration: 'underline',
                border: 'none',
                background: 'none',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                textTransform: 'capitalize',
                color: '#00CECF',
                padding: '0',
                cursor: 'pointer', // Enable pointer events for the "Choose file" button
              }}
            >
              <FormattedMessage defaultMessage='Choose files' />
            </Button>
          </Typography>
        </Box>
      </Box>

      {/* List of Added file attached to the Form */}
      {selectedFiles.length > 0 && (
        <ul
          className='add-file-custom-scrollbar'
          style={{
            listStyleType: 'none',
            paddingLeft: 0,
            maxHeight: '7rem',
            overflowY: 'auto',
            paddingRight: '.5rem',
          }}
        >
          {selectedFiles.map((file, index) => (
            <li
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                borderBottom: '1px solid #ccc',
                padding: '1px',
              }}
            >
              {getFileTypeIcon(file.type)}
              <span
                style={{
                  marginLeft: '10px',
                  width: '100%',
                  fontSize: '.85rem',
                }}
              >
                {file.name}
              </span>
              <IconButton onClick={() => handleFileDelete(file)}>
                <img src={xDeleteIcon} alt='Logo' style={{ height: 25 }} />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
};

export default AddFileButton;
